import { useEffect, useMemo, useState } from "react";
import { Users, X, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * AssigneePicker — valitse yksi tai useampi yrityksen jäsen raportille.
 *
 * Props:
 *   - value: string[]        — valittujen user_id:iden lista
 *   - onChange: (ids) => void
 *   - excludeOwners?: bool   — oletus true: näytetään vain role=member (kenttätyöntekijät)
 *                              owner-roolin tilit työskentelevät harvoin kentällä
 *   - compact?: bool          — tiiviimpi (pillit vain)
 *   - testIdPrefix?: string
 */
export default function AssigneePicker({ value = [], onChange, excludeOwners = true, compact = false, testIdPrefix = "assignees" }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company/members");
        setMembers(data?.members || []);
      } catch (e) {
        setError(e?.response?.data?.detail || null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectable = useMemo(
    () => (excludeOwners ? members.filter((m) => m.role === "member") : members),
    [members, excludeOwners]
  );

  const toggle = (uid) => {
    const next = new Set(value);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    onChange(Array.from(next));
  };

  if (loading) {
    return (
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-center" data-testid={`${testIdPrefix}-loading`}>
        <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (selectable.length === 0) {
    return (
      <div className="rounded-xl bg-slate-50 border border-dashed border-slate-200 p-4 text-xs text-slate-500 leading-relaxed" data-testid={`${testIdPrefix}-empty`}>
        Ei kenttätyöntekijöitä tiimissä. Kutsu työntekijä kohdasta <span className="font-semibold">Asetukset → Tiimi</span>, niin voit määrätä hänet raportille.
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="flex flex-wrap gap-2">
        {selectable.map((m) => {
          const picked = value.includes(m.user_id);
          const label = m.name?.trim() || m.email;
          return (
            <button
              key={m.user_id}
              type="button"
              onClick={() => toggle(m.user_id)}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold border transition-colors ${
                picked
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
              }`}
              data-testid={`${testIdPrefix}-pill-${m.user_id}`}
              aria-pressed={picked}
            >
              {picked ? <Check className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5 opacity-60" />}
              <span className="max-w-[160px] truncate">{label}</span>
            </button>
          );
        })}
      </div>
      {!compact && value.length > 0 && (
        <div className="mt-2 text-xs text-slate-500" data-testid={`${testIdPrefix}-count`}>
          {value.length} {value.length === 1 ? "työntekijä valittu" : "työntekijää valittu"}
          <button
            type="button"
            onClick={() => onChange([])}
            className="ml-2 inline-flex items-center gap-1 text-slate-400 hover:text-red-600"
            data-testid={`${testIdPrefix}-clear`}
          >
            <X className="h-3 w-3" /> Tyhjennä
          </button>
        </div>
      )}
      {error && (
        <div className="mt-2 text-xs text-red-600" data-testid={`${testIdPrefix}-error`}>
          Jäsenlistan lataus epäonnistui.
        </div>
      )}
    </div>
  );
}
